import React, { useState, useRef } from "react";
import { FileText, Plus } from "lucide-react";
import StickyNote, { NOTE_COLORS } from "../molecules/StickyNote";
import BureauCard from "../atoms/BureauCard";
import { INK, INK_2, INK_3, BURGUNDY, BURGUNDY_SOFT, LINE } from "../bureauTokens";
import { httpClient } from "../../../HttpClient";
import { useEtatGlobal } from "../../EtatGlobal.js";

// "Mes notes" — vraie table BureauNote (voir bureauNoteController.js), plus
// de localStorage : chaque note appartient strictement à son auteur. La
// liste est chargée une seule fois à la connexion (EtatGlobal.js#fetchAll →
// fetchBureauNotes, y compris la création de la note d'exemple au tout
// premier chargement), pas à chaque montage de ce composant.
function NotesBoard({ onToast }) {
  const { bureauNotes: notes, setBureauNotes: setNotes, loadingBureauNotes: loading } =
    useEtatGlobal();
  const [showArchived, setShowArchived] = useState(false);
  const [dragId, setDragId] = useState(null);
  const textDebounceRef = useRef({});

  const active = notes.filter((n) => !n.archived);
  const archived = notes.filter((n) => n.archived);

  const patchNote = (id, patch) => {
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  };

  // Instantané : la note apparaît tout de suite (id temporaire), avant même
  // la réponse serveur ; le toast de confirmation, lui, n'arrive qu'une fois
  // la note réellement enregistrée en base (id remplacé par le vrai à ce
  // moment-là).
  const addNote = async () => {
    const color = NOTE_COLORS[active.length % NOTE_COLORS.length];
    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();
    setNotes((prev) => [{ id: tempId, text: "", color, archived: false, createdAt: now, updatedAt: now }, ...prev]);
    try {
      const { data } = await httpClient.post("/api/bureau-notes", { text: "", color });
      // Si l'utilisateur a déjà tapé du texte pendant la brève fenêtre
      // optimiste (avant que l'id temporaire soit remplacé par le vrai), on
      // le conserve — et on le re-synchronise avec le vrai id, sinon un
      // debounce en attente sur l'id temporaire échouerait silencieusement
      // et ce texte ne serait jamais persisté.
      setNotes((prev) => {
        const tempNote = prev.find((n) => n.id === tempId);
        const typedText = tempNote?.text || "";
        if (typedText) {
          clearTimeout(textDebounceRef.current[tempId]);
          httpClient
            .put(`/api/bureau-notes/${data.data.id}`, { text: typedText })
            .catch((putErr) => console.error("Erreur lors de la synchronisation du texte :", putErr));
        }
        return prev.map((n) => (n.id === tempId ? { ...data.data, text: typedText || data.data.text } : n));
      });
      onToast && onToast("Note créée");
    } catch (err) {
      console.error("Erreur lors de la création de la note :", err);
      setNotes((prev) => prev.filter((n) => n.id !== tempId));
      onToast && onToast("Erreur lors de la création de la note");
    }
  };

  // Le texte se tape caractère par caractère : la mise à jour locale est
  // immédiate, l'appel serveur est débounce (600 ms d'inactivité) pour ne
  // pas envoyer une requête à chaque frappe.
  const setText = (id, text) => {
    patchNote(id, { text });
    clearTimeout(textDebounceRef.current[id]);
    textDebounceRef.current[id] = setTimeout(() => {
      httpClient
        .put(`/api/bureau-notes/${id}`, { text })
        .catch((err) => console.error("Erreur lors de la mise à jour de la note :", err));
    }, 600);
  };

  const setColor = (id, color) => {
    patchNote(id, { color });
    httpClient
      .put(`/api/bureau-notes/${id}`, { color })
      .catch((err) => console.error("Erreur lors de la mise à jour de la note :", err));
  };

  const archiveNote = (id) => {
    patchNote(id, { archived: true });
    httpClient
      .put(`/api/bureau-notes/${id}`, { archived: true })
      .catch((err) => console.error("Erreur lors de l'archivage de la note :", err));
  };

  const restoreNote = (id) => {
    patchNote(id, { archived: false });
    httpClient
      .put(`/api/bureau-notes/${id}`, { archived: false })
      .catch((err) => console.error("Erreur lors de la restauration de la note :", err));
  };

  const removeNote = (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    httpClient.delete(`/api/bureau-notes/${id}`).catch((err) => console.error("Erreur lors de la suppression de la note :", err));
  };

  const onDrop = (overId) => {
    if (!dragId || dragId === overId) {
      setDragId(null);
      return;
    }
    const list = [...notes];
    const from = list.findIndex((n) => n.id === dragId);
    const to = list.findIndex((n) => n.id === overId);
    if (from < 0 || to < 0) {
      setDragId(null);
      return;
    }
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setNotes(list);
    setDragId(null);

    const activeOrderedIds = list.filter((n) => !n.archived).map((n) => n.id);
    httpClient
      .put("/api/bureau-notes/reorder", { orderedIds: activeOrderedIds })
      .catch((err) => console.error("Erreur lors de la réorganisation des notes :", err));
  };

  const [addHover, setAddHover] = useState(false);

  return (
    <BureauCard>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: INK }}>
          <FileText size={14} style={{ color: BURGUNDY }} /> Mes notes
        </div>
        <button
          onClick={addNote}
          onMouseEnter={() => setAddHover(true)}
          onMouseLeave={() => setAddHover(false)}
          className="flex items-center gap-2 text-white font-medium transition-colors"
          style={{
            background: addHover ? BURGUNDY_SOFT : BURGUNDY,
            borderRadius: 10,
            padding: "9px 14px",
            fontSize: 11,
          }}
        >
          <Plus size={12} /> Nouvelle note
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse"
              style={{ background: LINE, borderRadius: 4, minHeight: 148 }}
            />
          ))
        ) : (
          <>
            {active.map((n, index) => (
              <StickyNote
                key={n.id}
                note={n}
                index={index}
                draggable
                isDragging={dragId === n.id}
                onDragStart={() => setDragId(n.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(n.id)}
                onDragEnd={() => setDragId(null)}
                onTextChange={(text) => setText(n.id, text)}
                onColorChange={(color) => setColor(n.id, color)}
                onArchive={() => archiveNote(n.id)}
                onRemove={() => removeNote(n.id)}
              />
            ))}
            {active.length === 0 && (
              <div className="text-center text-xs py-6" style={{ color: INK_3 }}>
                Aucune note épinglée. Créez-en une !
              </div>
            )}
          </>
        )}
      </div>
      {archived.length > 0 && (
        <div className="mt-3.5 pt-3" style={{ borderTop: `1px solid ${LINE}` }}>
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs font-semibold"
            style={{ color: INK_2 }}
          >
            Notes archivées ({archived.length})
          </button>
          {showArchived && (
            <div className="mt-2.5 space-y-1.5">
              {archived.map((n) => (
                <div
                  key={n.id}
                  className="flex items-center gap-2 text-xs rounded-lg"
                  style={{ color: INK_2, background: "#FAF7F4", padding: "7px 10px" }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: n.color }}
                  />
                  <span className="truncate flex-1">{n.text || "(note vide)"}</span>
                  <button onClick={() => restoreNote(n.id)} style={{ color: BURGUNDY }} className="hover:underline">
                    Restaurer
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </BureauCard>
  );
}

export default NotesBoard;
